import { DOM_EVENTS } from "@/constants/dom";

export interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Creates an image element from a base64 string
 */
const createImage = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve: (value: HTMLImageElement) => void, reject: (reason: Error) => void) => {
    const image = new Image();
    image.addEventListener(DOM_EVENTS.LOAD, () => resolve(image));
    image.addEventListener(DOM_EVENTS.ERROR, (error: ErrorEvent) => reject(new Error(error.message || "Image failed to load")));
    image.src = url;
  });
};

/**
 * Crops a base64 image using canvas
 * @param imageSrc - Base64 image string (with data URL prefix)
 * @param pixelCrop - Crop coordinates and dimensions
 * @param imageType - MIME type for output (default: image/jpeg)
 * @param quality - Image quality 0-1 (default: 0.95)
 * @returns Promise<string> - Cropped image as base64 (without data URL prefix)
 */
export const getCroppedBase64 = async (
  imageSrc: string,
  pixelCrop: CropArea,
  imageType: string | undefined = "image/jpeg",
  quality: number | undefined = 0.95,
): Promise<string> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("Could not get canvas context");
  }

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height);

  const dataUrl = canvas.toDataURL(imageType, quality);
  const base64 = dataUrl.split(",")[1];

  return base64;
};

/**
 * Gets the full data URL from a cropped image
 */
export const getCroppedDataUrl = async (
  imageSrc: string,
  pixelCrop: CropArea,
  imageType: string | undefined = "image/jpeg",
  quality: number | undefined = 0.95,
): Promise<string> => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("Could not get canvas context");
  }

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height);

  return canvas.toDataURL(imageType, quality);
};
